// Section grouping helpers shared by the builder canvas and the import workspace.
//
// A form is a FLAT `fields[]` in author order. A Fulcrum Section becomes a
// `kind:"display"` `section` field; the fields that belong to it carry
// `field.section = <section field key>` (nested sections point at their parent
// section). These helpers rebuild the nested tree from those markers.

import { SchemaField, SchemaRelation } from "../../types/fieldForm";

export type LayoutNode = { type: "field"; field: SchemaField } | { type: "section"; field: SchemaField; children: LayoutNode[] };

const isSectionField = (f: SchemaField) => f.kind === "display" && f.widget === "section";

/**
 * Canvas layout that also interleaves record-link relations. A relation renders
 * where its `section` (which section it lives in) and `after` (the sibling key it
 * follows) place it — mirroring how a field is positioned. This lets a relation be
 * dragged anywhere inside or outside a section.
 */
export type CanvasNode =
  | { type: "field"; field: SchemaField }
  | { type: "relation"; relation: SchemaRelation }
  | { type: "section"; field: SchemaField; children: CanvasNode[] };

/** A flat, top-to-bottom list of every canvas item (used to resolve drop anchors). */
export type FlatCanvasItem = { key: string; kind: "field" | "section" | "relation"; section: string | null; depth: number };

/**
 * Rebuild the nested section layout from a flat schema using each field's
 * `section` marker (the key of the `section` display field it belongs to).
 * Section fields precede their members in author order, so the parent node
 * always exists by the time its children are placed.
 */
export function buildSchemaLayout(fields: SchemaField[]): LayoutNode[] {
  const root: LayoutNode[] = [];
  const sectionNodeByKey = new Map<string, Extract<LayoutNode, { type: "section" }>>();

  for (const field of fields) {
    const parent = field.section ? sectionNodeByKey.get(field.section) : undefined;
    const siblings = parent ? parent.children : root;
    if (isSectionField(field)) {
      const node: Extract<LayoutNode, { type: "section" }> = { type: "section", field, children: [] };
      sectionNodeByKey.set(field.key, node);
      siblings.push(node);
    } else {
      siblings.push({ type: "field", field });
    }
  }

  return root;
}

/**
 * Per-field indent depth from the reconstructed layout (0 = top level). A
 * section header sits at its own depth; its members are one deeper. Lets the
 * sortable canvas indent correctly without nesting the DnD list.
 */
export function layoutDepths(nodes: LayoutNode[], depth = 0, acc: Map<string, number> = new Map()): Map<string, number> {
  for (const node of nodes) {
    acc.set(node.field.key, depth);
    if (node.type === "section") layoutDepths(node.children, depth + 1, acc);
  }
  return acc;
}

/**
 * Keys of every field nested under `sectionKey` (transitively), excluding the
 * section field itself. Used to delete/move a whole section as one unit.
 */
export function sectionDescendantKeys(fields: SchemaField[], sectionKey: string): string[] {
  const sectionParent = new Map<string, string | undefined>();
  for (const f of fields) {
    if (isSectionField(f)) sectionParent.set(f.key, f.section);
  }
  const isUnder = (start: string | undefined): boolean => {
    let cur = start;
    const seen = new Set<string>();
    while (cur && !seen.has(cur)) {
      if (cur === sectionKey) return true;
      seen.add(cur);
      cur = sectionParent.get(cur);
    }
    return false;
  };
  return fields.filter((f) => f.key !== sectionKey && isUnder(f.section)).map((f) => f.key);
}

/**
 * Flat index at which a new field appended to the END of `sectionKey`'s body
 * should be inserted (i.e. just after the section's last descendant). A section
 * block is contiguous in author order, so this is `lastDescendantIndex + 1`
 * (or `headerIndex + 1` for an empty section). Used to drop a field inside a
 * section and to position the in-section drop guideline.
 */
export function sectionInsertEndIndex(fields: SchemaField[], sectionKey: string): number {
  const headerIdx = fields.findIndex((f) => f.key === sectionKey);
  if (headerIdx === -1) return fields.length;
  const descendants = new Set(sectionDescendantKeys(fields, sectionKey));
  let last = headerIdx;
  for (let i = headerIdx + 1; i < fields.length; i++) {
    if (descendants.has(fields[i].key)) last = i;
  }
  return last + 1;
}

/**
 * Rebuild the nested layout including relations. Fields keep their author order
 * (with section membership); each relation is inserted into its `section`
 * container immediately after the sibling named by its `after` anchor (or at the
 * start when `after` is empty, or at the end when the anchor is missing). The
 * pass loop lets a relation be anchored after another relation.
 */
export function buildCanvasLayout(fields: SchemaField[], relations: SchemaRelation[] = []): CanvasNode[] {
  const root: CanvasNode[] = [];
  const sectionNodeByKey = new Map<string, Extract<CanvasNode, { type: "section" }>>();

  for (const field of fields) {
    const parent = field.section ? sectionNodeByKey.get(field.section) : undefined;
    const siblings = parent ? parent.children : root;
    if (isSectionField(field)) {
      const node: Extract<CanvasNode, { type: "section" }> = { type: "section", field, children: [] };
      sectionNodeByKey.set(field.key, node);
      siblings.push(node);
    } else {
      siblings.push({ type: "field", field });
    }
  }

  const containerFor = (section?: string): CanvasNode[] =>
    section && sectionNodeByKey.has(section) ? sectionNodeByKey.get(section)!.children : root;

  let pending = [...relations];
  for (let pass = 0; pass <= relations.length && pending.length > 0; pass++) {
    const deferred: SchemaRelation[] = [];
    for (const rel of pending) {
      const container = containerFor(rel.section);
      const node: CanvasNode = { type: "relation", relation: rel };
      if (rel.after == null) {
        // Default (no anchor): append at the END of the container so fields render
        // above unanchored/imported record-links. Explicit `after` still interleaves.
        container.push(node);
        continue;
      }
      const idx = container.findIndex((n) => (n.type === "relation" ? n.relation.key === rel.after : n.field.key === rel.after));
      if (idx === -1) {
        deferred.push(rel); // anchor (maybe another relation) not placed yet
        continue;
      }
      container.splice(idx + 1, 0, node);
    }
    if (deferred.length === pending.length) {
      // No progress — append the rest at the end of their container.
      for (const rel of deferred) containerFor(rel.section).push({ type: "relation", relation: rel });
      break;
    }
    pending = deferred;
  }

  return root;
}

/** Flatten the canvas layout to a top-to-bottom list (for drop-anchor resolution). */
export function flattenCanvas(fields: SchemaField[], relations: SchemaRelation[] = []): FlatCanvasItem[] {
  const out: FlatCanvasItem[] = [];
  const walk = (nodes: CanvasNode[], section: string | null, depth: number) => {
    for (const n of nodes) {
      if (n.type === "relation") out.push({ key: n.relation.key, kind: "relation", section, depth });
      else if (n.type === "section") {
        out.push({ key: n.field.key, kind: "section", section, depth });
        walk(n.children, n.field.key, depth + 1);
      } else out.push({ key: n.field.key, kind: "field", section, depth });
    }
  };
  walk(buildCanvasLayout(fields, relations), null, 0);
  return out;
}
